import { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import noop from 'utils/noop';
import { CREATE_TILESET_MODAL, NODE_TYPES } from 'modules/workflows/constants';
import {
  getFlexMetricsDateRange,
  getIsInProgressTotalPublishedView,
  getWorkflow,
  openModal,
  selectCampaignMediumsInWorkflow,
} from 'modules/workflows/ducks';
import { HelpToggletip } from 'ui/tooltip';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { selectDoesUserHaveRbacPermission } from 'core/userContext';
import { colors } from 'ui/theme';
import { useWorkflowContext } from 'modules/workflows/context';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button, SecondaryButton } from 'ui/button';
import { selectSendActivity } from 'modules/workflowIndex/selectors';
import { ChildNode, Node, Edge } from 'modules/workflows/types';
import { sendNodeUtils } from 'modules/workflows/utils';
import { ActionMenu } from 'ui/actionMenu';
import { MENU_DIRECTIONS } from 'ui/actionMenu/types';
import { ChevronUp16 } from 'ui/icons';
import { NodeSelectionActions, NodeFooterBar, SelectedNodesText } from '../Footer.styles';
import { TilesetsBar } from './TilesetSelectionBar';
import { InsightsButton } from '../../WorkflowAnalytics/InsightsButton';
import { getAnalyticsLink } from '../../WorkflowAnalytics/NodeAnalyticsCard/NodeAnalyticsCard.utils';

export type CloningCallbackProps = {
  edgesToClone: Edge[];
  nodesToClone: Node[];
};

export type DeletionCallbackProps = {
  edgesToDelete: Edge[];
  nodesToDelete: Node[];
};

export type NodeSelectionBarProps = {
  /** Whether RBAC 'can draft messaging' permission is enabled */
  canDraftMessaging: boolean;
  /** Whether start node is included in selection */
  isStartNodeSelected: boolean;
  /** Callback handles opening the clone node modal */
  onOpenCloneNodeModal?: (selectedItems: CloningCallbackProps) => void;
  /** Callback that handles opening the delete node modal */
  onOpenDeleteNodeModal?: (selectedItems: DeletionCallbackProps) => void;
  /** Selected edges */
  selectedEdges: Edge[];
  /** Selected nodes */
  selectedNodes: Node[];
  /** Is Published selection bar */
  isPublishedSelectionBar?: boolean;
};

export const NODE_SELECTION_BAR_TEST_ID = 'node-selection-bar';
export const CLONE_TEST_ID = 'open-clone-node-modal';
export const DELETE_TEST_ID = 'open-delete-node-modal';
export const START_NODE_TOGGLETIP_ID = 'start-node-toggletip';
export const VIEW_EXIT_RULE_BUTTON_TEST_ID = 'view-exit-rule-button';
export const INSIGHTS_ACTION_MENU_TEST_ID = 'insights-action-menu';

export const NodeSelectionBar: FC<React.PropsWithChildren<NodeSelectionBarProps>> = ({
  canDraftMessaging = false,
  isStartNodeSelected = false,
  onOpenCloneNodeModal = noop,
  onOpenDeleteNodeModal = noop,
  selectedEdges,
  selectedNodes,
  isPublishedSelectionBar = false,
}: NodeSelectionBarProps): JSX.Element => {
  const isInProgressTotalPublishedViewEnabled = useSelector(getIsInProgressTotalPublishedView);
  const dispatch = useDispatch();
  const { id } = useSelector(getWorkflow);
  const sendActivity = useSelector(selectSendActivity(id));
  const exitRuleExitsByNode = sendActivity?.exitRuleExitsByNode || {};
  const nodeIds = Object.keys(exitRuleExitsByNode || {});

  const showViewExitRuleButton = useMemo(() => {
    const selectedNodeId = selectedNodes[0]?.id;
    return selectedNodes.length === 1 && nodeIds.includes(selectedNodeId);
  }, [selectedNodes, nodeIds]);

  const { setShowExitSlideover } = useWorkflowContext();

  const handleTilesetClick = () =>
    dispatch(
      openModal(CREATE_TILESET_MODAL, {
        selectedNodes,
        selectedEdges,
      }),
    );

  const handleDuplicateClick = () =>
    onOpenCloneNodeModal({ nodesToClone: selectedNodes, edgesToClone: selectedEdges });

  const handleDeleteClick = () =>
    onOpenDeleteNodeModal({
      nodesToDelete: selectedNodes,
      edgesToDelete: selectedEdges,
    });

  const dateRange = useSelector(getFlexMetricsDateRange);

  const mediumsInWorkflow = useSelector(selectCampaignMediumsInWorkflow);
  const canViewReports = useSelector((state) =>
    // @ts-ignore
    selectDoesUserHaveRbacPermission(state)(PROJECT_PERMISSIONS.ViewReports),
  );
  const canViewInsights = useMemo(
    () => canViewReports && !!mediumsInWorkflow?.length,
    [canViewReports, mediumsInWorkflow],
  );

  const insightsButtons = useMemo(() => {
    const node = selectedNodes?.[0];

    if (!canViewInsights) return null;
    if (!sendNodeUtils.isSendNode(node?.type)) return null;
    if (node?.type === NODE_TYPES.CHANNEL_OPT) {
      const campaignNodes = node?.nodeData?.childNodes || [];
      if (campaignNodes.length > 1) {
        const options = campaignNodes.map((campaignNode: ChildNode) => ({
          as: 'a',
          content: `View ${campaignNode.defaultTitle} insights`,
          id: campaignNode?.nodeData?.campaignId,
          target: '_blank',
          href: getAnalyticsLink(campaignNode?.nodeData?.campaignId, dateRange),
        }));

        return (
          <ActionMenu
            data-test={INSIGHTS_ACTION_MENU_TEST_ID}
            direction={MENU_DIRECTIONS.TOP}
            options={options}
          >
            <SecondaryButton>
              View message insights <ChevronUp16 />
            </SecondaryButton>
          </ActionMenu>
        );
      } else if (campaignNodes.length === 1) {
        const n = campaignNodes[0];
        return (
          <InsightsButton to={getAnalyticsLink(n?.nodeData?.campaignId, dateRange)}>
            View {(n?.defaultTitle || '').toLowerCase()} insights
          </InsightsButton>
        );
      } else {
        return null;
      }
    }

    return (
      <InsightsButton to={getAnalyticsLink(node?.nodeData?.campaignId, dateRange)}>
        Campaign analytics
      </InsightsButton>
    );
  }, [canViewInsights, selectedNodes, dateRange]);

  return (
    <NodeFooterBar data-test={NODE_SELECTION_BAR_TEST_ID}>
      <NodeSelectionActions>
        {canDraftMessaging && (
          <>
            <SelectedNodesText>
              {selectedNodes.length} {selectedNodes.length > 1 ? 'tiles' : 'tile'} selected
              {isStartNodeSelected && (
                <HelpToggletip
                  tooltipContent={'The start tile cannot be cloned or deleted'}
                  iconProps={{ 'data-test': START_NODE_TOGGLETIP_ID, color: colors.secondaryText }}
                />
              )}
            </SelectedNodesText>

            <TilesetsBar
              isPublishedSelectionBar={isPublishedSelectionBar}
              handleTilesetClick={handleTilesetClick}
              handleDuplicateClick={handleDuplicateClick}
              handleDeleteClick={handleDeleteClick}
            >
              {showViewExitRuleButton && (
                <Button
                  data-test={VIEW_EXIT_RULE_BUTTON_TEST_ID}
                  size={BUTTON_SIZES.LARGE}
                  variant={BUTTON_VARIANTS.SECONDARY}
                  onClick={() => setShowExitSlideover(true)}
                >
                  View exit rule breakdown
                </Button>
              )}
              {isInProgressTotalPublishedViewEnabled && isPublishedSelectionBar && insightsButtons}
            </TilesetsBar>
          </>
        )}
      </NodeSelectionActions>
    </NodeFooterBar>
  );
};
